import React, { useState, useMemo } from 'react'
import { router, Link } from '@inertiajs/react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { formatClockHour, bandSentence, runningSinceLabel } from '@/lib/clearance-utils'
import { formatRate } from '@/lib/calendar-utils'
import { AlertTriangle, BedDouble, CalendarOff, Clock, MoonStar, SquarePen, TrendingDown } from 'lucide-react'
import type { SharedProps } from '@/types/shared'

/**
 * Last-Minute Clearance — the rule card (docs/plans/2026-08-13-last-minute-clearance.md §7).
 *
 * PREDICTABILITY IS THE PRODUCT (§3.1). This page exists so an operator can read tonight's
 * behaviour off it BEFORE it happens — the module was born from a correctly-behaving engine that
 * merely LOOKED asleep. When the rule was a hand-entered schedule that meant showing the rungs
 * back. The pressure ratchet has no schedule to show: every rung is sized by the night itself. So
 * the card keeps the same promise a truer way — the WORST CASE, the exact line the engine walks if
 * nothing sells, in the operator's own money.
 *
 * THE LINE ARRIVES COMPUTED. `ResolveClearanceRuleMutation` walks it with the engine's own
 * descent and its own rounder, so this page does no arithmetic on a price, a percentage or an
 * hour — it renders what it is sent. A projection drawn here would be a second implementation of
 * the ratchet, and the first thing it would do is disagree with it.
 *
 * THE FIRING BUDGET ADVISES; IT NEVER STOPS (operator ruling, 2026-08-14, superseding plan §3.7).
 * A room over its threshold keeps clearing — the amber row says its everyday price is wrong and
 * points at where to fix it. So no copy on this page may say the rule pauses, stops or steps aside,
 * and the badge is a description of the room ("Clearing often"), not a state of the rule.
 *
 * SKIP TONIGHT IS THE ONE EXCEPTION, AND IT IS THE OPERATOR'S OWN (§4.5). The rule never stands
 * aside by itself; the operator may stand it aside for one night. `skippedTonight` arrives from the
 * server — the same row the engine gates on — so the card never infers the state from an absent
 * descent, which is also what a sold-out night and a closed market look like. The word is **skip**,
 * never "pause": "Paused" belongs to the TopBar's property push badge and to nothing else (§12).
 */

interface ClearanceRung {
  hour: number
  discountPct: number
  rateAmount: number
  stepNumber: number
}

/**
 * Where the line ends, and which of the three brakes ends it — resolved by the engine, because
 * "never below" is a promise and the card must not make one nobody set. `floor` is null for a room
 * with no minimum rate (the column's default is 0.00, and a £0 floor is no floor), and the terminal
 * then names the operator's own limit or the engine's own.
 */
interface ClearanceTerminal {
  rateAmount: number
  discountPct: number
  source: 'floor' | 'operator_limit' | 'engine_limit'
}

interface ClearanceDescent {
  roomTypeId: number
  roomName: string
  propertyName: string | null
  currency: string
  fromHour: number
  lastOrderHour: number
  unsoldUnits: number
  familyCapacity: number
  standingDepthPct: number | null
  currentRate: number | null
  preClearanceRate: number | null
  floor: number | null
  terminal: ClearanceTerminal | null
  /**
   * What the line is DOING, decided by the engine (§12 rule 4 in spirit: this page renders, it
   * does not classify). `saturated` is not an absence — the descent has reached the bound that
   * ends it, and the terminal is what is left of the line.
   */
  state: 'stepping' | 'saturated' | 'market_closed'
  rungs: ClearanceRung[]
}

/** A room whose price for tonight the operator set themselves. Clearance leaves those alone. */
interface ClearanceRoomRef {
  roomTypeId: number
  name: string
}

interface ClearanceFamily {
  roomTypeId: number
  name: string
  propertyId: number
  propertyName: string | null
  firedCount: number
  cap: number | null
  /** Over the advisory threshold. A recommendation to re-price the room — never a stop. */
  advisory: boolean
}

interface ClearanceProps extends SharedProps {
  enabled: boolean
  /**
   * When the rule started running, ISO, already in the property's own zone. null until the switch
   * has been flipped (or backfilled) — the card then says nothing rather than dating the hotel to
   * whenever the row was last saved.
   */
  enabledAt: string | null
  /** The no-sales line for tonight, already walked by the engine. null when there is none to draw. */
  descent: ClearanceDescent | null
  maxDepthPct: number | null
  maxDepthBounds: [number, number]
  rungBounds: [number, number]
  bandLeads: number[]
  bandLeadDaysOverride: number | null
  bandMaxLeadDays: number
  lastOrderHour: number
  lastOrderHourSource: 'override' | 'measured' | 'standard'
  lastOrderHourOverride: number | null
  /** When the selling day opens. Nothing steps before it — 3am is not an hour anything sells in. */
  dayStartHour: number
  budgetCap: number | null
  budgetWindowNights: number
  /** The operator vetoed tonight from this card. Server truth, never inferred here (§4.5). */
  skippedTonight: boolean
  /** Rooms priced by hand for tonight — named rather than drawn a line for (§3.6). */
  pricedByHand: ClearanceRoomRef[]
  /** Why there is no line, when there is none. Server-classified; this page never derives it. */
  noLineReason: 'out_of_band' | 'priced_by_hand' | 'nothing_to_clear' | null
  families: ClearanceFamily[]
  roomsSetupPath: string
}

const AUTO = 'auto'
const NO_LIMIT = 'none'

/**
 * WHAT ENDS THE LINE, WORDED AS THE CONDITIONAL IT IS.
 *
 * "your own minimum" read as a fact about the room forever; it is a fact about a setting, and the
 * moment an operator moves that setting the line moves with it — Amsterdam's £49 room was selling
 * at £60 inside an open window minutes after its floor was raised (2026-09-02 audit, §4 P2). The
 * price is right, the promise was not, so the bound names the moment it is true of.
 */
function terminalLabel(terminal: ClearanceTerminal): string {
  if (terminal.source === 'floor') return 'your own minimum, as it stands now'
  if (terminal.source === 'operator_limit') return `your own ${terminal.discountPct}% limit`
  return `the engine's own ${terminal.discountPct}% limit`
}

function SectionCard({
  title,
  help,
  description,
  delay,
  action,
  children,
}: {
  title: string
  help?: string
  description?: React.ReactNode
  delay: number
  action?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <div
      className="animate-slide-in rounded-xl border border-border/60 bg-white shadow-sm"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="p-6">
        <div className="mb-2 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-1">
              <h3 className="text-sm font-semibold text-foreground">{title}</h3>
              {help && <HelpTooltip content={help} />}
            </div>
            {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
          </div>
          {action}
        </div>
        {children}
      </div>
    </div>
  )
}

export default function Clearance({
  currentUser,
  property,
  properties,
  enabled: initialEnabled,
  enabledAt,
  descent,
  maxDepthPct: initialMaxDepth,
  maxDepthBounds,
  rungBounds,
  bandLeads,
  bandLeadDaysOverride: initialBandOverride,
  bandMaxLeadDays,
  lastOrderHour,
  lastOrderHourSource,
  lastOrderHourOverride: initialLastOrderOverride,
  dayStartHour,
  budgetCap: initialBudgetCap,
  budgetWindowNights,
  skippedTonight,
  pricedByHand,
  noLineReason,
  families,
  roomsSetupPath,
}: ClearanceProps) {
  const base = property.basePath

  const [enabled, setEnabled] = useState(initialEnabled)
  const [skipping, setSkipping] = useState(false)
  const [lastOrderChoice, setLastOrderChoice] = useState(
    initialLastOrderOverride != null ? initialLastOrderOverride.toString() : AUTO
  )
  const [bandChoice, setBandChoice] = useState(
    initialBandOverride != null ? initialBandOverride.toString() : AUTO
  )
  const [maxDepthChoice, setMaxDepthChoice] = useState(
    initialMaxDepth != null ? initialMaxDepth.toString() : NO_LIMIT
  )
  const [budgetCap, setBudgetCap] = useState((initialBudgetCap ?? 8).toString())
  const [saving, setSaving] = useState(false)
  const review = usePricingReview()

  const isDirty = useMemo(() => {
    if (enabled !== initialEnabled) return true
    if (lastOrderChoice !== (initialLastOrderOverride != null ? initialLastOrderOverride.toString() : AUTO)) return true
    if (bandChoice !== (initialBandOverride != null ? initialBandOverride.toString() : AUTO)) return true
    if (maxDepthChoice !== (initialMaxDepth != null ? initialMaxDepth.toString() : NO_LIMIT)) return true
    if (budgetCap !== (initialBudgetCap ?? 8).toString()) return true
    return false
  }, [
    enabled,
    lastOrderChoice,
    bandChoice,
    maxDepthChoice,
    budgetCap,
    initialEnabled,
    initialLastOrderOverride,
    initialBandOverride,
    initialMaxDepth,
    initialBudgetCap,
  ])

  // Reviewed on the server first (change safety R1.5), then sent by the same PATCH. "Skip tonight"
  // below is not a rule save and keeps its own one-tap path (spec §1, non-goals).
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      clearance_enabled: enabled,
      clearance_last_order_hour: lastOrderChoice === AUTO ? null : Number(lastOrderChoice),
      clearance_band_lead_days: bandChoice === AUTO ? null : Number(bandChoice),
      clearance_max_depth_pct: maxDepthChoice === NO_LIMIT ? null : Number(maxDepthChoice),
      clearance_budget_cap: Number(budgetCap),
    }
    review.open(`${base}/pricing-rules/clearance/review`, payload, () =>
      inertiaSave('patch', `${base}/pricing-rules/clearance`, payload, {
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      })
    )
  }

  // The veto acts on the SAVED rule, so it reads the server's `initialEnabled` rather than the
  // switch's unsaved position: there is nothing to skip on a rule that is off, and nothing to skip
  // on a night the band does not reach. Both cases hide the button rather than disabling it —
  // a control that cannot do anything is not a control.
  const canSkipTonight = initialEnabled && bandLeads.includes(0)

  // HOW LONG IT HAS BEEN DOING THIS, read off the SAVED rule rather than the switch's unsaved
  // position — an operator mid-way through turning the rule off has not stopped running it yet.
  const runningSince = initialEnabled ? runningSinceLabel(enabledAt) : null

  function submitSkip(next: boolean) {
    setSkipping(true)
    const url = `${base}/pricing-rules/clearance/skip`
    const done = { onFinish: () => setSkipping(false) }
    // The VERB is the instruction. No date and no toggle travel with it: the server resolves
    // "tonight" from the property's own clock, so a tab left open past midnight cannot veto the
    // wrong night.
    if (next) router.post(url, {}, done)
    else router.delete(url, done)
  }

  // The rooms the operator priced themselves, named. Used both as the whole answer (when there is
  // no line to draw at all) and as a quiet note beside a line drawn for a DIFFERENT room, so a card
  // showing one room's descent never implies the rule is running on all of them.
  const pricedByHandNames = (
    <>
      {pricedByHand.map((room, i) => (
        <React.Fragment key={room.roomTypeId}>
          {i > 0 && (i === pricedByHand.length - 1 ? ' and ' : ', ')}
          {room.name}
        </React.Fragment>
      ))}
    </>
  )
  const pricedByHandNote = pricedByHand.length > 0 && (
    <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
      You set tonight's price for {pricedByHandNames} yourself, so clearance leaves{' '}
      {pricedByHand.length === 1 ? 'that room' : 'those rooms'} alone.
    </p>
  )

  const dormant = bandLeads.length === 0
  const furthestLead = dormant ? 0 : Math.max(...bandLeads)
  const advisedFamilies = families.filter((f) => f.advisory)
  const isGrouped = new Set(families.map((f) => f.propertyId)).size > 1
  const [minRungPct, maxRungPct] = rungBounds
  // The knob's choices come from the backend's own bounds, in the operator's direction. A Select
  // rather than a free number: the model refuses anything shallower than one rung, and a control
  // that can produce an error the operator cannot see coming is not a control.
  const maxDepthChoices = useMemo(() => {
    const [floorPct, ceilingPct] = maxDepthBounds
    const options: number[] = []
    for (let pct = floorPct; pct <= ceilingPct; pct += 5) options.push(pct)
    return options
  }, [maxDepthBounds])

  const lastOrderNote =
    lastOrderHourSource === 'override'
      ? 'You set this hour yourself.'
      : lastOrderHourSource === 'measured'
        ? 'Measured from when your own same-day bookings actually came in.'
        : 'Too few same-day bookings so far to measure yours, so this uses the usual closing time.'

  return (
    <OperatorLayout
      title="Last-Minute Clearance"
      currentUser={currentUser}
      property={property}
      properties={properties}
    >
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="clearance"
          help={{
            content:
              'Clearance is the one rule that lowers a price because a room is about to go empty. Close to arrival, if the night is heading for unsold rooms, it steps the price down along a line you can read in advance — and the price goes back to normal when the rooms are gone, or when the night stops heading for empty rooms.',
            learnMore: '/guides/clearance',
          }}
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Enable */}
          <SectionCard
            title="Last-Minute Clearance"
            help="Only rooms still unsold close to arrival are touched, and only when the night is heading for empty rooms. Your minimum room rate and your price floor still hold, and a price you set by hand is never touched."
            description="When rooms are still unsold close to arrival and the night is heading for empty rooms, the engine steps the price down once an hour — gently while there is time to sell, harder as the evening runs out. The price goes back to normal when the rooms are gone — or when the night stops heading for empty rooms."
            delay={100}
            action={
              <div className="flex shrink-0 items-center gap-3">
                {runningSince && (
                  <span className="whitespace-nowrap text-[11px] text-muted-foreground">
                    Running since {runningSince}
                  </span>
                )}
                <Switch checked={enabled} onCheckedChange={setEnabled} />
              </div>
            }
          >
            {enabled && dormant && (
              <div className="mt-4 flex gap-2.5 rounded-lg border-l-[3px] border-l-amber-400 bg-amber-50/60 px-3.5 py-3">
                <MoonStar className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div>
                  <p className="text-xs font-semibold text-amber-800">Nothing to clear here yet</p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-amber-700/90">
                    Your own booking history says the rooms for the next day or two are usually sold already,
                    so there is no late demand left to chase. Clearance stays quiet until that changes — or
                    pick the days yourself below.
                  </p>
                </div>
              </div>
            )}
          </SectionCard>

          {/* The worst case */}
          <SectionCard
            title="What happens tonight"
            help="This is the whole rule. Nothing else in the engine lowers a price because a room is unsold. While a night is clearing the price only steps down — unless you change the room's minimum rate or set the price yourself, which always win."
            description={
              // The promise of a line has to go with the line. A skipped night has nothing below
              // to read; nor has a night whose descent has reached the bound that ends it, which
              // is how a card headed "the line below…" came to sit above an empty state. The
              // sentence is spent only where there are steps still to come.
              descent?.state === 'stepping' && !skippedTonight ? (
                <>
                  Read this before it happens: the line below is what the engine does if nothing sells.{' '}
                  <span className="font-medium text-foreground">{bandSentence(bandLeads)}.</span>
                </>
              ) : (
                <span className="font-medium text-foreground">{bandSentence(bandLeads)}.</span>
              )
            }
            delay={150}
            action={
              canSkipTonight && !skippedTonight ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={skipping}
                    onClick={() => submitSkip(true)}
                    className="rounded-lg text-muted-foreground hover:text-foreground"
                  >
                    <CalendarOff className="mr-1.5 h-3.5 w-3.5" />
                    Skip tonight
                  </Button>
                  <HelpTooltip content="One tap, one night. Tonight's prices stay where normal pricing puts them, and clearance comes back tomorrow by itself. To stop one room instead, set its price by hand — a price you set is never touched." />
                </div>
              ) : undefined
            }
          >
            {skippedTonight ? (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3.5">
                <div className="flex gap-2.5">
                  <CalendarOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Tonight is skipped — no prices will be lowered.
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Clearance resumes tomorrow on its own.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={skipping}
                  onClick={() => submitSkip(false)}
                  className="rounded-lg text-muted-foreground hover:text-foreground"
                >
                  Resume tonight
                </Button>
              </div>
            ) : descent && descent.state === 'stepping' ? (
              <div className="mt-4">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <TrendingDown className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <span className="font-medium text-foreground">{descent.roomName}</span>
                  <span>
                    — {descent.unsoldUnits} of {descent.familyCapacity}{' '}
                    {descent.familyCapacity === 1 ? 'room' : 'rooms'} unsold right now
                  </span>
                </div>

                <div className="mt-2 overflow-x-auto">
                  <div
                    className={cn(
                      'flex min-w-max items-stretch rounded-lg border border-border/40',
                      enabled ? 'bg-white' : 'bg-muted/20'
                    )}
                  >
                    {descent.rungs.map((rung, i) => (
                      <div
                        key={rung.hour}
                        className={cn(
                          'flex flex-col items-center gap-0.5 px-4 py-3',
                          i > 0 && 'border-l border-border/40'
                        )}
                      >
                        <span className="text-[11px] text-muted-foreground">{formatClockHour(rung.hour)}</span>
                        <span className="text-sm font-semibold tabular-nums text-foreground">
                          {formatRate(rung.rateAmount, descent.currency)}
                        </span>
                        <span className="text-[11px] tabular-nums text-amber-700">{rung.discountPct}% off</span>
                      </div>
                    ))}
                    {descent.terminal && (
                      <div className="flex flex-col items-center justify-center gap-0.5 border-l border-border/40 bg-muted/30 px-4 py-3">
                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">never below</span>
                        <span className="text-sm font-semibold tabular-nums text-foreground">
                          {formatRate(descent.terminal.rateAmount, descent.currency)}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {terminalLabel(descent.terminal)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <p className="mt-3 text-xs leading-relaxed text-foreground">
                  Prices follow this line or better — every sale holds the next step for an hour, and selling
                  out ends it immediately. Two things outrank it: change this room's minimum rate, or set
                  tonight's price yourself, and the price moves there on the next run.
                </p>
                {/* The room has no minimum rate of its own, so the only thing under the line is the
                    engine's own limit. Said once, quietly, with the place it is fixed. */}
                {descent.floor == null && (
                  <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                    This room has no minimum rate of its own.{' '}
                    <Link href={roomsSetupPath} className="font-medium text-foreground underline">
                      Set a minimum room rate
                    </Link>{' '}
                    to put your own floor under this.
                  </p>
                )}
                {pricedByHandNote}
              </div>
            ) : descent && descent.state === 'saturated' && descent.terminal ? (
              /* THE END OF THE LINE IS STILL THE LINE (2026-09-02 audit, §4 P2). Amsterdam sat at
                 its bound under a header promising "the line below" with an empty state beneath
                 it. A descent with nowhere left to go has one price to show, and it is drawn the
                 same way every other price on this card is. */
              <div className="mt-4">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <TrendingDown className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <span className="font-medium text-foreground">{descent.roomName}</span>
                  <span>
                    — {descent.unsoldUnits} of {descent.familyCapacity}{' '}
                    {descent.familyCapacity === 1 ? 'room' : 'rooms'} unsold right now
                  </span>
                </div>

                <div className="mt-2 flex min-w-max max-w-max items-stretch rounded-lg border border-border/40 bg-muted/30">
                  <div className="flex flex-col items-center gap-0.5 px-4 py-3">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      as low as it goes
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-foreground">
                      {formatRate(descent.terminal.rateAmount, descent.currency)}
                    </span>
                    <span className="text-[11px] tabular-nums text-amber-700">
                      {descent.terminal.discountPct}% off
                    </span>
                  </div>
                  <div className="flex flex-col justify-center border-l border-border/40 px-4 py-3">
                    <span className="text-[11px] text-muted-foreground">
                      {terminalLabel(descent.terminal)}
                    </span>
                  </div>
                </div>

                <p className="mt-3 text-xs leading-relaxed text-foreground">
                  There are no further steps tonight — this is the bottom of the line. Two things still
                  outrank it: change this room's minimum rate, or set tonight's price yourself, and the
                  price moves there on the next run.
                </p>
                {descent.floor == null && (
                  <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                    This room has no minimum rate of its own.{' '}
                    <Link href={roomsSetupPath} className="font-medium text-foreground underline">
                      Set a minimum room rate
                    </Link>{' '}
                    to put your own floor under this.
                  </p>
                )}
                {pricedByHandNote}
              </div>
            ) : noLineReason === 'priced_by_hand' ? (
              /* A PRICE THE OPERATOR SET IS NOT THE RULE'S TO DESCEND FROM (§3.6). The card used to
                 draw its line off that row anyway — La Casa promised "5pm £50 · 6pm £49" for a night
                 pinned at £55 half an hour earlier, on a rule that had already withdrawn from it. */
              <div className="mt-4 flex gap-2.5 rounded-lg border border-border/60 bg-muted/40 px-4 py-3.5">
                <SquarePen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    You set tonight's price for {pricedByHandNames} yourself.
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Clearance leaves a price you set alone, so there is no line to draw tonight. Remove
                    that price and the line comes back on the next run.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-dashed border-border bg-white px-4 py-6 text-center">
                <p className="text-sm font-medium text-foreground">Nothing to clear tonight</p>
                {/* An empty line has several causes and they are not interchangeable. Which one it
                    is arrives from the server — the card used to compare one hour against another
                    and tell an operator their last bookings hour had passed while it was still
                    three hours away, on the surface whose whole job is explaining. */}
                <p className="mt-1 text-xs text-muted-foreground">
                  {descent?.state === 'market_closed'
                    ? `Your last bookings usually come in at ${formatClockHour(lastOrderHour)}, and that hour has passed — a lower price tonight would sell nothing.`
                    : noLineReason === 'out_of_band'
                      ? 'Tonight\'s arrivals are not in the days below, so there is nothing here to clear. The line comes back for the nights that are.'
                      : 'Either every room for tonight is sold, or the engine has not priced tonight yet. The line comes back as soon as a night has rooms left to sell.'}
                </p>
              </div>
            )}

            <div className="mt-3 space-y-1.5">
              <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                <span>
                  Steps land an hour apart between{' '}
                  <strong className="text-foreground">{formatClockHour(dayStartHour)}</strong> and your last
                  bookings hour, and get bigger as the night runs out — each one takes between {minRungPct}%
                  and {maxRungPct}% off the engine's price for that night, deeper the more rooms are left and
                  the less time there is to sell them. The price on the shelf can move further than that in one
                  step, because the engine's own price for the night moves as well. Nothing changes overnight,
                  and nothing changes after{' '}
                  <strong className="text-foreground">{formatClockHour(lastOrderHour)}</strong> — that is when
                  your last bookings usually come in, so a cut later than that sells nothing. {lastOrderNote}
                </span>
              </p>
              {furthestLead > 0 && (
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Arrivals further ahead only ever get one opening step of {minRungPct}% off. The rest of the
                  line belongs to the arrival day itself, when the hours left to sell are that day's own.
                </p>
              )}
              {!dormant && !bandLeads.includes(0) && (
                <p className="text-[11px] leading-relaxed text-amber-700">
                  Tonight's arrivals are not among the days you clear — your rooms for tonight are usually gone
                  by now — so only the opening step ({minRungPct}% off) is ever used here. Pick the days
                  yourself below if you want tonight included.
                </p>
              )}
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                The price goes back to normal on the next run once the rooms are gone, or once the night
                stops heading for empty rooms. One sale with more rooms still going empty keeps the lower
                price working, and when the last room goes the price is back to normal straight away — so a
                late cancellation re-opens at the normal price.
              </p>
            </div>
          </SectionCard>

          {/* Settings */}
          <SectionCard
            title="Your settings"
            help="Leave these on the automatic setting and the engine reads them off your own bookings, re-checking as your history grows. Set one yourself and it stays exactly where you put it."
            description="Change these and the line above moves with them."
            delay={200}
          >
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Label className="w-56 shrink-0 text-sm text-muted-foreground">
                  Last bookings usually come in at
                </Label>
                <Select value={lastOrderChoice} onValueChange={setLastOrderChoice}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={AUTO}>Work it out from my bookings</SelectItem>
                    {Array.from({ length: 24 }, (_, hour) => (
                      <SelectItem key={hour} value={hour.toString()}>
                        {formatClockHour(hour)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">
                  The steps are counted back from this hour, so the lowest price still gets real time on sale.
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Label className="w-56 shrink-0 text-sm text-muted-foreground">Start clearing</Label>
                <Select value={bandChoice} onValueChange={setBandChoice}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={AUTO}>Work it out from my bookings</SelectItem>
                    {Array.from({ length: bandMaxLeadDays + 1 }, (_, days) => (
                      <SelectItem key={days} value={days.toString()}>
                        {days === 0
                          ? 'On the arrival day only'
                          : days === 1
                            ? 'From the day before arrival'
                            : `From ${days} days before arrival`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">
                  How early a night can start clearing. Left automatic, it opens only where your booking
                  curve still expects rooms to sell.
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Label className="w-56 shrink-0 text-sm text-muted-foreground">Never deeper than</Label>
                <Select value={maxDepthChoice} onValueChange={setMaxDepthChoice}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_LIMIT}>No limit — stops at your floor</SelectItem>
                    {maxDepthChoices.map((pct) => (
                      <SelectItem key={pct} value={pct.toString()}>
                        {pct}% off
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">
                  How far a night may fall in total. Your minimum room rate stops it either way — this is for
                  when you want it to stop sooner than that.
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Label className="w-56 shrink-0 text-sm text-muted-foreground">Tell me when a room clears more than</Label>
                <div className="relative w-24">
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    max="30"
                    className="text-center font-semibold tabular-nums"
                    value={budgetCap}
                    onChange={(e) => setBudgetCap(e.target.value)}
                  />
                </div>
                <span className="text-sm text-muted-foreground">
                  nights in any {budgetWindowNights}
                </span>
                <span className="text-xs text-muted-foreground">
                  A room that needs clearing this often is priced too high every day of the week. Clearance
                  keeps working while you sort that out — this only decides when it says so.
                </span>
              </div>
            </div>
          </SectionCard>

          {/* Budget state per family */}
          <SectionCard
            title="Rooms clearing lately"
            help="One line per room the rule can act on. A linked room follows its parent, so it is counted with the room it follows rather than on its own."
            description={`How often each room has needed clearing in the last ${budgetWindowNights} nights.`}
            delay={250}
          >
            {families.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
                <BedDouble className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm font-medium text-foreground">No rooms to clear yet</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Rooms appear here once they are set up and priced.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {families.map((family) => (
                  <div
                    key={family.roomTypeId}
                    className={cn(
                      'rounded-lg border px-4 py-3',
                      family.advisory ? 'border-amber-400/60 bg-amber-50/70' : 'border-border/40 bg-white'
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {family.advisory && <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />}
                        <span className="text-sm font-medium text-foreground">{family.name}</span>
                        {isGrouped && family.propertyName && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                            {family.propertyName}
                          </span>
                        )}
                      </div>
                      {/* An advised room's count is inside its note below — saying it twice in one
                          card is noise, so the right-hand column carries the state instead. */}
                      {family.advisory ? (
                        <span className="rounded bg-amber-200/70 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                          Clearing often
                        </span>
                      ) : (
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {family.firedCount === 0
                            ? `Hasn't cleared a night in the last ${budgetWindowNights}`
                            : `Cleared ${family.firedCount} of the last ${budgetWindowNights} nights`}
                        </span>
                      )}
                    </div>
                    {family.advisory && (
                      <p className="mt-1.5 text-[11px] leading-relaxed text-amber-700">
                        Cleared {family.firedCount} of the last {budgetWindowNights} nights. Clearance keeps
                        working, but a room that needs it this often is priced too high on its ordinary days —
                        lower its offset or base rate and it will stop needing rescue.{' '}
                        <Link href={roomsSetupPath} className="font-medium text-amber-800 underline">
                          Open Rooms Setup
                        </Link>
                      </p>
                    )}
                  </div>
                ))}
                {advisedFamilies.length === 0 && enabled && (
                  <p className="text-[11px] text-muted-foreground">
                    No room is clearing often enough to be worth a second look at its everyday price.
                  </p>
                )}
              </div>
            )}
          </SectionCard>

          <div className="flex justify-end">
            <Button type="submit" disabled={!isDirty || saving} className="rounded-lg px-6">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
      {review.dialog}
    </OperatorLayout>
  )
}
